import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthData {
  userId: string;
  userType: string;
  userName?: string;
  email?: string;
  accessToken: string;
  refreshToken: string;
  isSuperAdmin?: boolean;
}

interface AuthState {
  user: User | null;
  userId: string | null;
  userType: string | null;
  userName: string | null;
  email: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  isSuperAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const loadFromLocalStorage = (): Partial<AuthState> => {
  if (typeof window === 'undefined') return {};

  try {
    const authData = localStorage.getItem('instadev-auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      return {
        userId: parsed.userId || "68c411a6c89c81d47fae4e4e",
        userType: parsed.userType,
        userName: parsed.userName,
        email: parsed.email,
        accessToken: parsed.accessToken,
        refreshToken: parsed.refreshToken,
        isSuperAdmin: parsed.isSuperAdmin || false,
        isAuthenticated: !!parsed.userId,
      };
    }
  } catch (error) {
    console.error('Error loading auth data from localStorage:', error);
  }
  return {};
};

const saveToLocalStorage = (authData: Partial<AuthState>) => {
  if (typeof window === 'undefined') return;

  try {
    const dataToSave = {
      userId: authData.userId,
      userType: authData.userType,
      userName: authData.userName,
      email: authData.email,
      accessToken: authData.accessToken,
      refreshToken: authData.refreshToken,
      isSuperAdmin: authData.isSuperAdmin,
    };
    localStorage.setItem('instadev-auth', JSON.stringify(dataToSave));
  } catch (error) {
    console.error('Error saving auth data to localStorage:', error);
  }
};

const clearLocalStorage = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('instadev-auth');
};

const initialState: AuthState = {
  user: null,
  userId: null,
  userType: null,
  userName: null,
  email: null,
  accessToken: null,
  refreshToken: null,
  isSuperAdmin: false,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  ...loadFromLocalStorage(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<User>) => {
      state.isLoading = false;
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    setAuthData: (state, action: PayloadAction<AuthData>) => {
      state.userId = action.payload.userId;
      state.userType = action.payload.userType;
      state.userName = action.payload.userName || null;
      state.email = action.payload.email || null;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isSuperAdmin = action.payload.isSuperAdmin || false;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
      saveToLocalStorage(state);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.user = null;
      state.userId = null;
      state.userType = null;
      state.userName = null;
      state.email = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isSuperAdmin = false;
      state.isAuthenticated = false;
      state.error = action.payload;
      clearLocalStorage();
    },
    logout: (state) => {
      state.user = null;
      state.userId = null;
      state.userType = null;
      state.userName = null;
      state.email = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isSuperAdmin = false;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      clearLocalStorage();
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const { loginStart, loginSuccess, setAuthData, loginFailure, logout, clearError } = authSlice.actions;
export default authSlice.reducer;